import { z } from "zod";
import { assertThat } from "../../utils/assert-that.ts";
import type { Organisation } from "../organisation/organisation.ts";
import type { grafitUserSchema, grafitUserWithRolesSchema } from "./user.validation.ts";

export const USER_ROLE = z.enum(["ADMIN", "PROJECT_MANAGER", "EDITOR"]);
export type UserRole = z.infer<typeof USER_ROLE>;
export const FALLBACK_USER_ROLES: UserRole[] = ["EDITOR"];
export const UserRolesSchema = z.array(USER_ROLE);
export function parseUserRoles(roles: unknown) {
  const rolesResult = UserRolesSchema.safeParse(roles);
  return rolesResult.success ? rolesResult.data : FALLBACK_USER_ROLES;
}

export const PERMISSIONS = z.enum([
  //admin
  "user.set.role",
  "organisation.update",
  "organisation.task-catalog.structure-edit",

  //rest
  "project.owner.set",
  "project.users.set",
  "project.list-all",
  "company.manage",
  "organisation.templates.manage",
  "organisation.bidrow-templates.manage",
]);
export type Permissions = z.infer<typeof PERMISSIONS>;

const ROLE_PERMISSIONS: Record<UserRole, Set<Permissions>> = {
  ADMIN: new Set(Object.values(PERMISSIONS.enum)),
  PROJECT_MANAGER: new Set([
    "project.owner.set",
    "project.users.set",
    "project.list-all",
    "company.manage",
    "organisation.templates.manage",
    "organisation.bidrow-templates.manage",
  ]),
  EDITOR: new Set([]),
};

export function canUserDo(roles: UserRole[], permissionToCheck: Permissions): boolean {
  return roles.some((role) => ROLE_PERMISSIONS[role].has(permissionToCheck));
}

export type GrafitUser = z.infer<typeof grafitUserSchema>;
export type GrafitUserWithRoles = z.infer<typeof grafitUserWithRolesSchema>;
export type GrafitUserId = GrafitUser["id"];

export interface AdminUserListItem extends GrafitUserWithRoles {
  lastName: string;
  createdAt: Date;
  updatedAt: Date;
  banned: boolean;
  lastSignInAt: Date | undefined;
  lastActiveAt: Date | undefined;
  organisation: (Pick<Organisation, "id"> & { name: string }) | null;
}

interface NameInitialSource {
  fullName: string;
  email: string | null;
}

export const getUserInitial = (user: NameInitialSource): string => {
  const source = (user.fullName || user.email) ?? "";
  assertThat(source.length > 0, "Cannot get user initial: user has empty name and email");
  return source.charAt(0).toUpperCase();
};

export const TRIGRAM_CHARS_REPLACE_RE = /[^A-Z0-9]/g;
export const TRIGRAM_VALID_RE = /^[A-Z0-9]{3}$/;
const sanitizeForTrigram = (s: string): string =>
  s.normalize("NFD").toUpperCase().replace(TRIGRAM_CHARS_REPLACE_RE, "");
const TRIGRAM_DIGIT_PADDING = "1234567890";

export const getDefaultTrigram = (fullName: string): string => {
  const names = fullName.trim().split(/\s+/).filter(Boolean);
  const firstName = names[0];
  const lastName = names[names.length - 1];

  if (!firstName) {
    return "AAA";
  }

  if (names.length === 1 || !lastName) {
    return sanitizeForTrigram(firstName + TRIGRAM_DIGIT_PADDING).slice(0, 3);
  }

  const first = sanitizeForTrigram(firstName + TRIGRAM_DIGIT_PADDING);
  const last = sanitizeForTrigram(lastName + TRIGRAM_DIGIT_PADDING);

  return first.charAt(0) + last.slice(0, 2);
};

export const generateTrigramCandidates = ({ fullName, email }: { fullName: string; email: string }): string[] => {
  const namingPartOfEmail = email.split("@")[0] ?? "";
  const namingPartWithoutDot = namingPartOfEmail.replace(/\./g, "");

  const name = fullName.trim() || namingPartWithoutDot;

  const defaultTrigram = getDefaultTrigram(name);
  const candidates: string[] = [defaultTrigram];

  const base = defaultTrigram.slice(0, 2);

  const names = name.trim().split(/\s+/).filter(Boolean);
  let lastName = names[names.length - 1];

  if (!lastName) {
    lastName = TRIGRAM_DIGIT_PADDING;
  } else {
    lastName = sanitizeForTrigram(lastName + TRIGRAM_DIGIT_PADDING);
  }

  const firstLetterNonIncludedInDefaultTrigramIndex = names.length > 1 ? 2 : 3;

  for (let i = firstLetterNonIncludedInDefaultTrigramIndex; i < 10; i++) {
    const letter = lastName[i]?.toUpperCase();
    if (!letter) {
      break;
    }
    const sanitizedLetter = sanitizeForTrigram(letter).slice(0, 1);
    candidates.push((base + sanitizedLetter).slice(0, 3));
  }
  return candidates;
};

export const getUserDisplayName = (user: Pick<GrafitUser, "fullName" | "email">): string => {
  const fullName = user.fullName.trim();
  if (fullName) {
    return fullName;
  }

  const localPart = user.email.trim().split("@")[0];
  const firstPart = localPart?.split(".")[0];
  return firstPart ? firstPart.charAt(0).toUpperCase() + firstPart.slice(1) : "";
};

export const getFirstName = (user: Pick<GrafitUser, "fullName" | "email">): string => {
  return getUserDisplayName(user).split(/\s+/)[0] ?? "";
};

export const isGrafitEmail = (email: string): boolean => {
  return ["@grafit.pro", "@grafit.fr"].some((domain) => email.endsWith(domain));
};

export interface UserData {
  phoneNumber: string | null;
  jobTitle: string | null;
  trigram: string;
  tenderRefCount: number;
  tenderRefYear: number;
}

export interface UserRolesAndData {
  roles: UserRole[];
  data: UserData | null;
}
